import { Container } from '@cleartrip/ct-design-components'
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { TUPLE_COLORS, TUPLE_TOOLTIP } from '../config/tupleTheme'

export interface RevampTooltipProps {
  children: React.ReactNode
  content: React.ReactNode
  position?: 'top' | 'bottom'
  align?: 'center' | 'left' | 'right'
  disabled?: boolean
  className?: string
  style?: React.CSSProperties
  maxWidth?: number | string
  padding?: number | string
  wrapperDisplay?: 'inline-flex' | 'flex' | 'block'
  gap?: number
  isOpen?: boolean
}

const VIEWPORT_PAD = 12
const { padding: TOOLTIP_PADDING, radius: TOOLTIP_RADIUS, gap: GAP, arrowWidth: ARROW_W, arrowHeight: ARROW_H, zIndex: TOOLTIP_Z } =
  TUPLE_TOOLTIP

/** Centered on the tooltip; one static object per side so no per-position CSS rule is minted. */
const ARROW_BASE_CSS = {
  position: 'absolute',
  left: '50%',
  transform: 'translateX(-50%)',
  width: ARROW_W,
  height: ARROW_H,
  lineHeight: 0,
} as const
const ARROW_CSS = {
  top: { ...ARROW_BASE_CSS, bottom: -ARROW_H },
  bottom: { ...ARROW_BASE_CSS, top: -ARROW_H },
} as const

const TooltipArrow: React.FC<{ placement: 'top' | 'bottom'; fill: string }> = ({ placement, fill }) => (
  <svg fill='none' height={ARROW_H} viewBox={`0 0 ${ARROW_W} ${ARROW_H}`} width={ARROW_W} aria-hidden>
    {placement === 'top' ? (
      <path d={`M0 0L${ARROW_W / 2} ${ARROW_H}L${ARROW_W} 0H0Z`} fill={fill} />
    ) : (
      <path d={`M0 ${ARROW_H}L${ARROW_W / 2} 0L${ARROW_W} ${ARROW_H}H0Z`} fill={fill} />
    )}
  </svg>
)

const RevampTooltip: React.FC<RevampTooltipProps> = ({
  children,
  content,
  position = 'top',
  align = 'center',
  disabled = false,
  className = '',
  style,
  maxWidth = 420,
  padding = TOOLTIP_PADDING,
  wrapperDisplay = 'inline-flex',
  isOpen: controlledOpen,
  gap = GAP,
}) => {
  const [internalHover, setInternalHover] = useState(false)
  const [isDismissed, setIsDismissed] = useState(false)
  const [placement, setPlacement] = useState<'top' | 'bottom'>(position)

  const anchorRef = useRef<HTMLDivElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (controlledOpen) {
      setIsDismissed(false)
    }
  }, [controlledOpen])

  const isOpen = !disabled && Boolean(content) && !isDismissed && (Boolean(controlledOpen) || internalHover)

  const close = useCallback(() => {
    setInternalHover(false)
    setIsDismissed(true)
  }, [])

  const updatePosition = useCallback(() => {
    const tooltip = tooltipRef.current
    if (!anchorRef.current || !tooltip) return
    const anchorRect = anchorRef.current.getBoundingClientRect()
    const tooltipRect = tooltip.getBoundingClientRect()

    const anchorCenterX = anchorRect.left + anchorRect.width / 2

    let nextPlacement: 'top' | 'bottom' = position
    let top = 0

    if (position === 'top') {
      top = anchorRect.top - tooltipRect.height - gap
      if (top < VIEWPORT_PAD) {
        top = anchorRect.bottom + gap
        nextPlacement = 'bottom'
      }
    } else {
      top = anchorRect.bottom + gap
      if (top + tooltipRect.height > window.innerHeight - VIEWPORT_PAD) {
        top = anchorRect.top - tooltipRect.height - gap
        nextPlacement = 'top'
      }
    }

    let left = 0
    if (align === 'left') {
      left = anchorRect.left
    } else if (align === 'right') {
      left = anchorRect.right - tooltipRect.width
    } else {
      left = anchorCenterX - tooltipRect.width / 2
    }

    const maxLeft = window.innerWidth - tooltipRect.width - VIEWPORT_PAD
    left = Math.max(VIEWPORT_PAD, Math.min(left, Math.max(VIEWPORT_PAD, maxLeft)))

    // Written to the element, not through Container `css`: every distinct position would
    // otherwise mint a new styled-components rule that is never removed (see RangeSlider).
    tooltip.style.top = `${top}px`
    tooltip.style.left = `${left}px`
    tooltip.style.visibility = 'visible'
    setPlacement(nextPlacement)
  }, [position, align, gap])

  useLayoutEffect(() => {
    if (isOpen) {
      updatePosition()
    }
  }, [isOpen, updatePosition, content])

  useEffect(() => {
    if (!isOpen) return
    const handleDismiss = () => close()
    window.addEventListener('scroll', handleDismiss, true)
    window.addEventListener('resize', handleDismiss)
    return () => {
      window.removeEventListener('scroll', handleDismiss, true)
      window.removeEventListener('resize', handleDismiss)
    }
  }, [isOpen, close])

  const handleMouseEnter = () => {
    if (disabled || !content) return
    setIsDismissed(false)
    setInternalHover(true)
  }

  const handleMouseLeave = () => {
    setInternalHover(false)
  }

  if (disabled || !content) {
    return <>{children}</>
  }

  const bg = TUPLE_COLORS.tooltipBg

  // Static per instance (maxWidth/padding vary by caller); position is set in updatePosition.
  const tooltipCss = {
    position: 'fixed',
    top: -9999,
    left: -9999,
    maxWidth,
    backgroundColor: bg,
    color: TUPLE_COLORS.tooltipText,
    borderRadius: TOOLTIP_RADIUS,
    padding,
    zIndex: TOOLTIP_Z,
    pointerEvents: 'none',
    visibility: 'hidden',
    boxSizing: 'border-box',
  } as const

  const tooltipElement =
    isOpen && typeof document !== 'undefined'
      ? createPortal(
          <Container ref={tooltipRef} css={tooltipCss}>
            {content}
            <Container css={ARROW_CSS[placement]}>
              <TooltipArrow fill={bg} placement={placement} />
            </Container>
          </Container>,
          document.body
        )
      : null

  return (
    <Container
      ref={anchorRef}
      className={className}
      css={{
        display: wrapperDisplay,
        maxWidth: '100%',
        minWidth: 0,
        ...style,
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {tooltipElement}
    </Container>
  )
}

export default React.memo(RevampTooltip)
